/**
 * Magento-parity carousel for Nosto DynamicCard placements.
 * Theme-owned because Nosto-injected <script> tags often do not execute.
 *
 * Config:
 * - slidesPerView() → visible cards (keep in sync with CSS --mg-slides-per-view)
 * Arrow click advances by the same count.
 */
(() => {
  const ROOT_IDS = [
    'frontpage-nosto-trending-test',
    'frontpage-nosto-1',
    'category-nosto-also-like',
    'product-nosto-also-like',
  ];
  /** @type {WeakSet<HTMLElement>} */
  const initialized = new WeakSet();
  let scanQueued = false;
  let mutating = false;

  function slidesPerView() {
    const w = window.innerWidth;
    if (w < 769) return 2;
    if (w < 1025) return 3;
    if (w < 1280) return 4;
    return 5;
  }

  /**
   * @param {ParentNode} root
   * @returns {HTMLElement | null}
   */
  function findTrack(root) {
    const track =
      root.querySelector('[data-mg-nosto-track]') ||
      root.querySelector('.mg-nosto-carousel') ||
      root.querySelector('ul.product-grid');
    return track instanceof HTMLElement ? track : null;
  }

  /**
   * @param {ParentNode} track
   * @returns {HTMLElement[]}
   */
  function realSlides(track) {
    return [
      ...track.querySelectorAll(
        '.mg-nosto-slide:not(.mg-nosto-slide--clone), :scope > li:not(.mg-nosto-slide--clone)'
      ),
    ].filter((slide) => slide instanceof HTMLElement);
  }

  /**
   * @param {Element} root
   */
  function measureHostWidth(root) {
    const appBlock = root.closest('.shopify-block, .shopify-app-block');
    const host =
      (appBlock && appBlock.parentElement) ||
      root.closest('.layout-panel-flex') ||
      root.closest('.shopify-section') ||
      root.parentElement ||
      document.documentElement;

    const hostW = Math.floor(host.getBoundingClientRect().width);
    return Math.max(0, Math.min(hostW, window.innerWidth, 1600));
  }

  /**
   * @param {HTMLElement} track
   * @param {Element} root
   */
  function measureViewportWidth(track, root) {
    const wrap = track.closest('.mg-nosto-carousel-wrap');
    const wrapStyles = wrap ? window.getComputedStyle(wrap) : null;
    const gutter = wrapStyles
      ? (parseFloat(wrapStyles.paddingLeft) || 0) + (parseFloat(wrapStyles.paddingRight) || 0)
      : 0;

    const wrapW = wrap ? Math.floor(wrap.getBoundingClientRect().width) : 0;
    const hostW = measureHostWidth(root);
    const base = wrapW > 0 ? Math.min(wrapW, hostW) : hostW;
    return Math.max(0, Math.floor(base - gutter));
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} track
   */
  function applySlideWidths(root, track) {
    const perView = slidesPerView();
    root.style.setProperty('--mg-slides-per-view', String(perView));
    root.dataset.mgSlidesPerView = String(perView);

    const hostW = measureHostWidth(root);
    if (hostW > 0) {
      root.style.setProperty('width', '100%', 'important');
      root.style.setProperty('max-width', `${hostW}px`, 'important');
    }

    const appBlock = root.closest('.shopify-block, .shopify-app-block');
    if (appBlock instanceof HTMLElement) {
      appBlock.style.setProperty('width', '100%', 'important');
      appBlock.style.setProperty('max-width', '100%', 'important');
      appBlock.style.setProperty('min-width', '0', 'important');
      appBlock.style.setProperty('overflow', 'visible', 'important');
    }

    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const trackWidth = measureViewportWidth(track, root);
    if (trackWidth <= 0) return;

    const slideWidth = Math.floor((trackWidth - gap * (perView - 1)) / perView);
    if (slideWidth <= 0) return;

    const px = `${slideWidth}px`;
    realSlides(track).forEach((slide) => {
      slide.style.boxSizing = 'border-box';
      slide.style.flex = `0 0 ${px}`;
      slide.style.width = px;
      slide.style.maxWidth = px;
      slide.style.minWidth = px;
    });

    const step = slideWidth + gap;
    const page = step > 0 ? Math.round(track.scrollLeft / (step * perView)) : 0;
    track.scrollLeft = Math.max(0, page * step * perView);
  }

  /**
   * @param {HTMLElement} wrap
   * @param {HTMLElement} track
   */
  function ensureViewport(wrap, track) {
    if (track.parentElement?.classList.contains('mg-nosto-carousel-viewport')) {
      return track.parentElement;
    }

    const viewport = document.createElement('div');
    viewport.className = 'mg-nosto-carousel-viewport';

    if (track.parentElement === wrap) {
      wrap.insertBefore(viewport, track);
    } else {
      wrap.appendChild(viewport);
    }
    viewport.appendChild(track);
    return viewport;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  function ensureStructure(root) {
    if (root.querySelector('.mg-nosto-carousel-wrap')) {
      const track = findTrack(root);
      const wrap = root.querySelector('.mg-nosto-carousel-wrap');
      if (track && wrap instanceof HTMLElement) ensureViewport(wrap, track);
      return track;
    }

    const track = findTrack(root);
    if (!track) return null;

    const title =
      root.querySelector('.mg-nosto-title') ||
      root.querySelector('.nosto-title') ||
      root.querySelector(':scope > h2, :scope > h3, :scope > h4');

    const outer = document.createElement('div');
    outer.className = 'mg-nosto-outer';

    const wrap = document.createElement('div');
    wrap.className = 'mg-nosto-carousel-wrap';

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'mg-nosto-arrow mg-nosto-arrow--prev';
    prev.setAttribute('aria-label', 'Previous');
    prev.setAttribute('data-mg-nosto-prev', '');
    prev.innerHTML =
      '<svg class="mg-nosto-arrow__icon" viewBox="0 0 24 45" fill="none" aria-hidden="true" focusable="false"><path d="M23 1 L1 22.5 L23 44"/></svg><span class="visually-hidden">Previous</span>';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'mg-nosto-arrow mg-nosto-arrow--next';
    next.setAttribute('aria-label', 'Next');
    next.setAttribute('data-mg-nosto-next', '');
    next.innerHTML =
      '<svg class="mg-nosto-arrow__icon" viewBox="0 0 24 45" fill="none" aria-hidden="true" focusable="false"><path d="M1 1 L23 22.5 L1 44"/></svg><span class="visually-hidden">Next</span>';

    track.classList.add('mg-nosto-carousel');
    track.setAttribute('data-mg-nosto-track', '');

    Array.from(track.children).forEach((li) => {
      li.classList.add('mg-nosto-slide');
    });

    if (title) {
      title.classList.add('mg-nosto-title');
      outer.appendChild(title);
    }

    wrap.appendChild(prev);
    wrap.appendChild(track);
    ensureViewport(wrap, track);
    wrap.appendChild(next);
    outer.appendChild(wrap);
    root.appendChild(outer);

    return track;
  }

  /**
   * @param {HTMLElement} track
   */
  function slideStep(track) {
    const slide = realSlides(track)[0];
    if (!slide) return Math.max(track.clientWidth, 1);
    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  /**
   * @param {HTMLElement} track
   */
  function maxScroll(track) {
    return Math.max(0, track.scrollWidth - track.clientWidth);
  }

  /**
   * @param {HTMLElement} track
   */
  function canLoop(track) {
    return realSlides(track).length > slidesPerView() && maxScroll(track) > 2;
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} track
   */
  function updateControls(root, track) {
    const prev = root.querySelector('[data-mg-nosto-prev]');
    const next = root.querySelector('[data-mg-nosto-next]');
    const fits = realSlides(track).length <= slidesPerView();
    const loop = canLoop(track);
    const max = maxScroll(track);

    root.classList.toggle('mg-nosto--fits', fits);

    [prev, next].forEach((button, index) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.hidden = fits;
      button.setAttribute('aria-hidden', fits ? 'true' : 'false');
      button.tabIndex = fits ? -1 : 0;
      if (fits) {
        button.disabled = true;
        return;
      }
      const atStart = track.scrollLeft <= 2 || max <= 2;
      const atEnd = track.scrollLeft >= max - 2 || max <= 2;
      button.disabled = !loop && (index === 0 ? atStart : atEnd);
    });
  }

  const ROW_HAS_SIZE_CLASS = 'mg-plp-add-to-bag--row-has-size';
  const TITLE_SELECTOR = "a[ref='productTitleLink'] .text-block, a.contents .text-block";

  /**
   * @param {HTMLElement} slide
   * @returns {HTMLElement[]}
   */
  function titleBoxes(slide) {
    /** @type {HTMLElement[]} */
    const boxes = [];
    const block = slide.querySelector(TITLE_SELECTOR);
    const heading = slide.querySelector('p[role="heading"]');
    if (block instanceof HTMLElement) boxes.push(block);
    if (heading instanceof HTMLElement && heading !== block) boxes.push(heading);
    return boxes;
  }

  /**
   * Combined title + bundle-value line height (Magento name stack).
   * @param {HTMLElement} slide
   */
  function titleStackHeight(slide) {
    const boxes = titleBoxes(slide);
    if (boxes.length === 0) return 0;
    const top = Math.min(...boxes.map((box) => box.getBoundingClientRect().top));
    const bottom = Math.max(...boxes.map((box) => box.getBoundingClientRect().bottom));
    return Math.max(0, bottom - top);
  }

  /**
   * Ensure cards without size chips reserve the same vertical space as the
   * tallest .mg-plp-size-options in the visible page (so ATC lines up).
   * @param {HTMLElement} slot
   * @param {number} chipH
   */
  function syncChipReserve(slot, chipH) {
    const existing = slot.querySelector('[data-mg-chip-reserve]');
    if (slot.querySelector('.mg-plp-size-options')) {
      if (existing) existing.remove();
      return;
    }
    if (chipH <= 0) {
      if (existing) existing.remove();
      return;
    }
    const reserve = existing instanceof HTMLElement ? existing : document.createElement('div');
    if (!(existing instanceof HTMLElement)) {
      reserve.setAttribute('data-mg-chip-reserve', '');
      reserve.setAttribute('aria-hidden', 'true');
      slot.insertBefore(reserve, slot.firstChild);
    }
    reserve.style.cssText = `display:block;flex:0 0 auto;width:100%;height:${chipH}px;min-height:${chipH}px;pointer-events:none;`;
  }

  /**
   * @param {HTMLElement[]} slides
   * @param {number} perView
   * @returns {HTMLElement[][]}
   */
  function pageGroups(slides, perView) {
    /** @type {HTMLElement[][]} */
    const groups = [];
    for (let i = 0; i < slides.length; i += perView) groups.push(slides.slice(i, i + perView));
    if (groups.length > 1 && slides.length % perView !== 0) {
      const last = groups.pop();
      const prev = groups.pop();
      if (last && prev) groups.push(prev.concat(last));
    }
    return groups;
  }

  /**
   * Mandatory snap must include the last page, not only every full page.
   * A short final page (7 cards at 4 per view) otherwise has no reachable
   * snap point, and the browser pulls every arrow click back to the start.
   * @param {HTMLElement[]} slides
   * @param {number} perView
   */
  function applySnapAlign(slides, perView) {
    const snapIndexes = new Set();
    for (let index = 0; index < slides.length; index += perView) snapIndexes.add(index);
    if (slides.length > perView) snapIndexes.add(slides.length - perView);
    slides.forEach((slide, index) => {
      slide.style.setProperty('scroll-snap-align', snapIndexes.has(index) ? 'start' : 'none', 'important');
    });
  }

  /**
   * Magento-parity: equalize title stack + chip row + card height for the visible page only.
   * Lives here because Nosto-injected card scripts do not execute; carousel always loads.
   *
   * Never lock .mg-nosto-carousel-viewport to a fixed height — Nosto template uses
   * overflow:hidden there, so a short lock clips ATC after chips/images settle.
   * @param {HTMLElement} track
   */
  function syncSlideHeights(track) {
    const all = realSlides(track).filter((s) => s instanceof HTMLElement);
    if (all.length === 0) return;
    const viewport = track.closest('.mg-nosto-carousel-viewport');

    all.forEach((slide) => {
      slide.style.height = '';
      slide.style.minHeight = '';
      titleBoxes(slide).forEach((box) => {
        box.style.minHeight = '';
      });
      const chips = slide.querySelector('.mg-plp-size-options');
      if (chips instanceof HTMLElement) chips.style.minHeight = '';
      const slot = slide.querySelector('mg-plp-add-to-bag');
      if (slot instanceof HTMLElement) {
        slot.classList.remove(ROW_HAS_SIZE_CLASS);
        slot.style.minHeight = '';
        const reserve = slot.querySelector('[data-mg-chip-reserve]');
        if (reserve) reserve.remove();
      }
    });
    // Clear any legacy fixed height that clipped ATC.
    if (viewport instanceof HTMLElement) {
      viewport.style.height = '';
      viewport.style.minHeight = '';
      viewport.style.maxHeight = '';
    }

    const perView = slidesPerView();
    applySnapAlign(all, perView);

    const groups = pageGroups(all, perView);

    groups.map(measureTitlesAndChips).forEach(applyTitlesAndChips);

    // Force layout after chip reserves so ATC is included in the measured height.
    void track.offsetHeight;

    const maxSlides = groups.map((group) =>
      Math.max(
        0,
        ...group.map((slide) => {
          const atc = slide.querySelector('mg-plp-add-to-bag');
          const rect = slide.getBoundingClientRect();
          const atcBottom = atc instanceof HTMLElement ? atc.getBoundingClientRect().bottom : rect.bottom;
          return Math.max(rect.height, atcBottom - rect.top);
        })
      )
    );

    groups.forEach((group, index) => {
      const maxSlide = maxSlides[index];
      if (maxSlide == null || maxSlide <= 0) return;
      const px = `${Math.ceil(maxSlide)}px`;
      group.forEach((slide) => {
        // min-height only: fixed height + overflow:hidden clips ATC.
        slide.style.minHeight = px;
      });
    });
  }

  /**
   * @param {HTMLElement[]} slides
   */
  function measureTitlesAndChips(slides) {
    const maxStack = Math.max(0, ...slides.map(titleStackHeight));
    const titles = slides.map((slide) => {
      const boxes = titleBoxes(slide);
      const headingH = boxes
        .slice(1)
        .reduce((sum, box) => sum + box.getBoundingClientRect().height, 0);
      return { boxes, headingH };
    });

    const slots = slides
      .map((slide) => slide.querySelector('mg-plp-add-to-bag'))
      .filter((slot) => slot instanceof HTMLElement);
    const chipRows = slides
      .map((slide) => slide.querySelector('.mg-plp-size-options'))
      .filter((el) => el instanceof HTMLElement);
    // Equalize .mg-plp-size-options to the tallest visible chip row (1 or 2 wraps).
    const maxChipH = Math.ceil(
      Math.max(0, ...chipRows.map((el) => el.getBoundingClientRect().height))
    );

    return { maxStack, titles, slots, chipRows, maxChipH };
  }

  /**
   * @param {{
   *   maxStack: number,
   *   titles: { boxes: HTMLElement[], headingH: number }[],
   *   slots: HTMLElement[],
   *   chipRows: HTMLElement[],
   *   maxChipH: number
   * }} measurement
   */
  function applyTitlesAndChips({ maxStack, titles, slots, chipRows, maxChipH }) {
    if (maxStack > 0) {
      titles.forEach(({ boxes, headingH }) => {
        const box = boxes[0];
        if (!box) return;
        box.style.minHeight = `${Math.ceil(maxStack - headingH)}px`;
      });
    }

    if (chipRows.length > 0) {
      chipRows.forEach((el) => {
        el.style.minHeight = `${maxChipH}px`;
      });

      slots.forEach((slot) => {
        slot.classList.add(ROW_HAS_SIZE_CLASS);
        syncChipReserve(slot, maxChipH);
      });
    }
  }

  /**
   * Re-run height match for currently visible slides. Carousel owns chip/ATC sync.
   * @param {HTMLElement | null} root
   * @param {HTMLElement | null} track
   */
  function notifyHeightSync(root, track) {
    if (root && track) syncSlideHeights(track);
    window.dispatchEvent(new CustomEvent('mg:plp-row-heights-sync'));
  }

  /** @type {WeakMap<HTMLElement, Set<number>>} */
  const pendingSyncs = new WeakMap();

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} track
   */
  function notifyHeightSyncSoon(root, track) {
    let pending = pendingSyncs.get(track);
    if (!pending) {
      pending = new Set();
      pendingSyncs.set(track, pending);
    }
    [50, 1200].forEach((ms) => {
      if (pending.has(ms)) return;
      pending.add(ms);
      window.setTimeout(() => {
        pending.delete(ms);
        notifyHeightSync(root, track);
      }, ms);
    });
  }

  /**
   * @param {HTMLElement} track
   * @param {number} dir
   */
  function scrollByPage(track, dir) {
    const step = slideStep(track) * slidesPerView();
    const page = step > 0 ? Math.round(track.scrollLeft / step) : 0;
    const max = maxScroll(track);
    const loop = canLoop(track);

    if (loop) {
      if (dir > 0 && track.scrollLeft >= max - 2) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      if (dir < 0 && track.scrollLeft <= 2) {
        track.scrollTo({ left: max, behavior: 'smooth' });
        return;
      }
    }

    track.scrollTo({
      left: Math.min(max, Math.max(0, (page + dir) * step)),
      behavior: 'smooth',
    });
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} track
   */
  function refreshLayout(root, track) {
    applySlideWidths(root, track);
    updateControls(root, track);
    // Force layout so height sync measures real slide widths (not 0 mid-flex).
    void track.offsetWidth;
    notifyHeightSyncSoon(root, track);
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} track
   */
  function bindCarousel(root, track) {
    if (initialized.has(root)) {
      refreshLayout(root, track);
      return;
    }

    initialized.add(root);

    const prev = root.querySelector('[data-mg-nosto-prev]');
    const next = root.querySelector('[data-mg-nosto-next]');

    if (prev) prev.addEventListener('click', () => scrollByPage(track, -1));
    if (next) next.addEventListener('click', () => scrollByPage(track, 1));

    let scrollTick = false;
    track.addEventListener(
      'scroll',
      () => {
        if (scrollTick) return;
        scrollTick = true;
        requestAnimationFrame(() => {
          scrollTick = false;
          updateControls(root, track);
        });
      },
      { passive: true }
    );

    let resizeTick = false;
    const scheduleRefresh = () => {
      if (resizeTick) return;
      resizeTick = true;
      window.setTimeout(() => {
        resizeTick = false;
        refreshLayout(root, track);
      }, 100);
    };

    window.addEventListener('resize', scheduleRefresh);

    // Observe width changes on the wrap (not viewport height — height sync would loop).
    const wrap = track.closest('.mg-nosto-carousel-wrap');
    if (wrap && typeof ResizeObserver !== 'undefined') {
      let lastW = Math.round(wrap.getBoundingClientRect().width);
      new ResizeObserver((entries) => {
        const w = Math.round(entries[0]?.contentRect?.width || wrap.getBoundingClientRect().width);
        if (Math.abs(w - lastW) < 2) return;
        lastW = w;
        scheduleRefresh();
      }).observe(wrap);
    }

    // Images / DynamicCard ATC often paint after first layout — re-sync heights.
    track.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', () => notifyHeightSync(root, track), { once: true });
    });

    if (typeof ResizeObserver !== 'undefined') {
      let resyncQueued = false;
      const slideObserver = new ResizeObserver(() => {
        if (resyncQueued) return;
        resyncQueued = true;
        requestAnimationFrame(() => {
          resyncQueued = false;
          notifyHeightSync(root, track);
        });
      });
      realSlides(track).forEach((slide) => slideObserver.observe(slide));
    }

    refreshLayout(root, track);
  }

  /**
   * @param {ParentNode} root
   */
  function hasRenderableCards(root) {
    return Boolean(
      root.querySelector('nosto-dynamic-card') ||
        root.querySelector('product-card') ||
        root.querySelector('.product-card') ||
        root.querySelector('ul.product-grid > li') ||
        root.querySelector('.mg-nosto-carousel > li')
    );
  }

  /**
   * @param {HTMLElement | null} root
   * @returns {boolean}
   */
  function initRoot(root) {
    if (!root || !hasRenderableCards(root)) return false;

    mutating = true;
    try {
      const track = ensureStructure(root);
      if (!track) return false;

      root.classList.add('mg-nosto-ready', 'mg-product-grid');
      bindCarousel(root, track);

      requestAnimationFrame(() => {
        mutating = true;
        try {
          refreshLayout(root, track);
          window.dispatchEvent(new Event('resize'));
        } finally {
          mutating = false;
        }
      });
    } finally {
      mutating = false;
    }

    return true;
  }

  /**
   * @returns {HTMLElement[]}
   */
  function collectRoots() {
    /** @type {Set<HTMLElement>} */
    const roots = new Set();

    ROOT_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) roots.add(el);
    });

    document.querySelectorAll('.nosto_element').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (
        el.querySelector('nosto-dynamic-card') ||
        el.querySelector('ul.product-grid') ||
        el.querySelector('.mg-nosto-carousel')
      ) {
        roots.add(el);
      }
    });

    return [...roots];
  }

  /**
   * @param {Element} marker
   */
  function removalTarget(marker) {
    const card = marker.closest('nosto-dynamic-card') || marker;
    const slide = card.closest('.mg-nosto-slide, .mg-nosto-serp-product');
    if (slide) return slide;

    const parent = card.parentElement;
    if (
      parent &&
      parent.childElementCount === 1 &&
      !parent.matches('body, #nosto-serp, #nosto-autocomplete, .nosto_element')
    ) {
      return parent;
    }

    return card;
  }

  /**
   * @param {ParentNode} scope
   * @returns {Element[]}
   */
  function sampleMarkers(scope) {
    /** @type {Element[]} */
    const found = [];
    /** @param {ParentNode} root */
    const visit = (root) => {
      if (!root || typeof root.querySelectorAll !== 'function') return;
      root.querySelectorAll('[data-mg-exclude-sample]').forEach((marker) => found.push(marker));
      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) visit(element.shadowRoot);
      });
    };
    visit(scope);
    return found;
  }

  /**
   * @param {ParentNode} [scope]
   */
  function removeSampleCards(scope) {
    const rootScope = scope || document;
    sampleMarkers(rootScope).forEach((marker) => {
      if (!marker.isConnected) return;

      const rootNode = marker.getRootNode();
      const cardHost = rootNode instanceof ShadowRoot ? rootNode.host : marker;
      const widget = cardHost.closest('.nosto_element');
      removalTarget(cardHost).remove();

      if (!(widget instanceof HTMLElement)) return;

      if (!widget.querySelector('nosto-dynamic-card, .product-card, .mg-pair-with-card')) {
        widget.hidden = true;
        return;
      }

      const track = findTrack(widget);
      if (track) refreshLayout(widget, track);
    });
  }

  function scan() {
    if (mutating) return;
    removeSampleCards(document);
    collectRoots().forEach((root) => initRoot(root));
  }

  function queueScan() {
    if (mutating || scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => {
      scanQueued = false;
      scan();
    });
  }

  function start() {
    scan();

    window.addEventListener('mg:nosto-carousel-heights-sync', () => {
      collectRoots().forEach((root) => {
        const track = findTrack(root);
        if (track) notifyHeightSyncSoon(root, track);
      });
    });

    const observer = new MutationObserver((mutations) => {
      if (mutating) return;

      const relevant = mutations.some((mutation) => {
        if (!(mutation.target instanceof Element)) return false;
        if (
          mutation.target.closest(
            '.mg-nosto-outer, .mg-nosto-carousel-wrap, .mg-nosto-carousel-viewport'
          )
        ) {
          return false;
        }
        return Boolean(
          mutation.target.closest('.nosto_element') ||
            mutation.target.classList?.contains('nosto_element') ||
            [...mutation.addedNodes].some(
              (node) =>
                node instanceof Element &&
                (node.id?.startsWith('frontpage-nosto') ||
                  node.classList?.contains('nosto_element') ||
                  node.querySelector?.('.nosto_element, nosto-dynamic-card'))
            )
        );
      });

      if (relevant) queueScan();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    document.addEventListener('@nosto/DynamicCard/loaded', (event) => {
      const host = event.target;
      if (!(host instanceof Element)) return;
      if (!host.querySelector('[data-mg-exclude-sample]')) return;
      removeSampleCards(host);
    });

    [500, 1500, 3000].forEach((ms) => {
      window.setTimeout(queueScan, ms);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
